import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { postFormAction } from "../../../_store/post.slice";
import { useDispatch, useSelector } from "react-redux";
import CalenderIcon from "../../../assets/svg/calander-icon-1.svg";
import userImage from "../../../assets/user.png";
import NoDataimg from "../../../assets/No_Data.png";
import { history } from "../../../_helpers";
import CommitteePostDetails from "./CommitteePostDetails";
import { showToastNotification } from "../../../components/common/ui/NotificationModal/ToastNotication";
import CustomLoader from "../../../components/common/ui/Loader";

function CommitteePostPage() {
  const dispatch = useDispatch();
  const { committeeID } = useParams();

  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentView, setCurrentView] = useState("list");
  const [viewPost, setViewPost] = useState("");
  const observer = useRef();

  const { loading: postLoading } = useSelector((x) => x.post);
  const ImageURL = process.env.REACT_APP_API_IMAGE_URL + "/";

  // ✅ Fetch posts function
  const fetchPosts = async (pageNumber, isScroll = false) => {
    if (!committeeID) return;

    isScroll ? setLoadingMore(true) : setLoading(true);

    try {
      const searchParams = `?committeeId=${committeeID}&page=${pageNumber}&limit=10&status=true`;
      const result = await dispatch(
        postFormAction.getPostFormList(searchParams)
      );

      const newPosts = result?.payload?.data?.posts || [];
      const totalPages = result?.payload?.data?.totalPages;

      setPosts((prev) => [...prev, ...newPosts]);
      setHasMore(pageNumber < totalPages);
      setPage((prev) => prev + 1);
    } catch (err) {
      showToastNotification("error", err.message || "Failed to fetch posts");
    } finally {
      isScroll ? setLoadingMore(false) : setLoading(false);
    }
  };

  // ✅ Initial fetch when committee changes
  useEffect(() => {
    setPosts([]);
    setPage(1);
    setHasMore(true);
    fetchPosts(1);
  }, [committeeID]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  // ✅ Infinite scroll observer
  const lastPostRef = useCallback(
    (node) => {
      if (loadingMore || loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchPosts(page, true);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loadingMore, loading, hasMore, page]
  );

  if (postLoading) return <CustomLoader />;

  return (
    <>
      {posts?.length > 0 ? (
        <section className="paddingtop-100">
          <div className="container">
            {posts?.map((post, index) => {
              const isLast = index === posts.length - 1;
              const plainText =
                post?.description?.replace(/<[^>]+>/g, "") || "";
              const isLongText = plainText.length > 600;

              return (
                <>
                  {currentView === "list" && (
                    <div
                      key={post._id}
                      ref={isLast ? lastPostRef : null}
                      style={{ marginBottom: "40px" }}
                    >
                      <h1 className="UserPostHeading">{post.title}</h1>
                      <p className="UserPostSubHeading">
                        <img src={CalenderIcon} alt="" height={"18px"} aria-hidden="true" />
                        &nbsp;
                        {new Date(post.createdAt).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                        ,{" "}
                        {new Date(post.createdAt).toLocaleTimeString("en-US", {
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })}
                        &nbsp;&nbsp;
                        <img src={userImage} alt="" height={"16px"} aria-hidden="true" />{" "}
                        {post.createdBy?.firstName} {post.createdBy?.lastName}
                      </p>

                      {/* ✅ If long text → show truncated text + button */}
                      {isLongText ? (
                        <>
                          <div
                            className="UserPostContent"
                            dangerouslySetInnerHTML={{
                              __html: plainText.substring(0, 600) + "...",
                            }}
                            style={{ wordBreak: "break-word" }}
                          ></div>
                          {post?.files?.length > 0 &&
                            post?.files?.map((file) => {
                              return (
                                <a
                                  target="_blank"
                                  href={`${ImageURL}${file?.file}`}
                                  rel="noopener noreferrer"
                                  aria-label={`Download document for ${post.title}`}
                                >
                                  <i
                                    className="fa fa-download"
                                    aria-hidden="true"
                                  ></i>{" "}
                                  Download document here
                                </a>
                              );
                            })}
                          <div className="mt-3">
                            <button
                              type="submit"
                              className="sign-in-button"
                              onClick={() => {
                                setCurrentView("details");
                                setViewPost(post);
                              }}
                            >
                              View Post
                              <i className="mx-2 css-arrow-icon bi bi-arrow-right"></i>
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* ✅ If short text → show full content + documents */}
                          <div
                            className="UserPostContent"
                            dangerouslySetInnerHTML={{
                              __html: post?.description,
                            }}
                            style={{ wordBreak: "break-word" }}
                          ></div>

                          {post?.files?.length > 0 && (
                            <div className="d-flex flex-column gap-2 mt-2">
                              {post.files.map((file) => (
                                <a
                                  key={file._id}
                                  href={`${ImageURL}${file?.file}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={`Download document for ${post.title}`}
                                >
                                  <i
                                    className="fa fa-download"
                                    aria-hidden="true"
                                  ></i>
                                  Download document here
                                </a>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                      <hr />
                    </div>
                  )}
                </>
              );
            })}

            {currentView === "details" && (
              <CommitteePostDetails
                post={viewPost}
                setCurrentView={setCurrentView}
              />
            )}
          </div>
        </section>
      ) : (
        <div className="sign-in-section">
          <div className="container">
            <div className="login-container">
              <div className="error-page-box p-5 d-flex flex-column justify-content-center align-items-center">
                <img src={NoDataimg} alt="404" />
                <h2 className="error-page-label">No Posts Found</h2>
                <button
                  onClick={() => {
                    history?.navigate("/Home");
                  }}
                  type="submit"
                  className="sign-in-button"
                >
                  Back to Home
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CommitteePostPage;
